import styled from "@emotion/styled";
import {
  addSeconds,
  endOfDay,
  endOfMonth,
  endOfToday,
  endOfYesterday,
  format,
  isValid,
  setMilliseconds,
  startOfDay,
  startOfMonth,
  startOfToday,
  startOfYesterday,
  subDays,
  subMonths,
} from "date-fns";
import { useCallback, useState } from "react";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  DatePicker,
  DatePickerRangeCalendarsWrapper,
  DatePickerRangeSelectInputs,
  RangeDatePicker,
  SelectField,
} from "@livechat/design-system";
import {
  Alert,
  Button,
  DesignToken,
  FormField,
  Heading,
  RadiusToken,
  SegmentedControl,
  ShadowToken,
  SpacingToken,
  Text,
  Textarea,
} from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import { StyledLink } from "components/links/StyledLink";
import { HelpCenter } from "constants/help-center";
import { ReportExportType } from "constants/reports/report-type";
import Role from "constants/role";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { isEmail } from "helpers/validate";
import { contentPadding } from "routes/styled";
import { selectLoggedInAgentLogin, selectLoggedInAgentPermission } from "store/entities/agents";
import { selectHasReportExportOption } from "store/entities/license";
import { ReportsViewActions, getIsReportGenerating } from "store/views/reports";
import { withVar } from "themes/helpers";

import { UpgradeReportPage } from "../components/UpgradeReportPage";

interface IDataRangeItemProps {
  label: string;
  value: { from: Date; to: Date };
  showSummary: boolean;
  isManual: boolean;
}

const validateEmails = (emailsText: string): string => {
  const emails = emailsText
    .split(",")
    .map(email => email.trim())
    .filter(Boolean);

  if (emails.length === 0) {
    return "Please enter a valid email address.";
  }

  if (emails.length > 20) {
    return "You can send report to maximum of 20 recipients.";
  }

  const invalidEmail = emails.find(email => !isEmail(email));
  return invalidEmail !== undefined ? `One of entered emails is incorrect (${invalidEmail}).` : "";
};

const generateSummaryText = (label: string, value: { from: Date; to: Date }): React.ReactNode => {
  switch (label) {
    case "Last 7 days":
      return (
        <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>
          {" "}
          ({format(value.from, "ddd, D MMM YYYY")} - {format(value.to, "ddd, D MMM YYYY")})
        </Text>
      );
    case "Last month":
      return <Text customColor={withVar(DesignToken.ContentBasicSecondary)}> ({format(value.from, "MMM YYYY")})</Text>;
    case "Last 3 months":
      return (
        <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>
          {" "}
          ({format(value.from, "ddd, D MMM YYYY")} - {format(value.to, "ddd, D MMM YYYY")})
        </Text>
      );
    default:
      return "";
  }
};

const DataRangeItem: React.FC<IDataRangeItemProps> = ({ label, value, showSummary }) => {
  const summaryText = showSummary ? generateSummaryText(label, value as { from: Date; to: Date }) : "";

  return (
    <div>
      {label}
      {summaryText}
    </div>
  );
};

const GenerateReport: React.FC = () => {
  const options = [
    { id: "yesterday", label: "Yesterday", value: { from: startOfYesterday(), to: endOfYesterday() } },
    {
      id: "last-7-days",
      label: "Last 7 days",
      value: { from: subDays(startOfToday(), 6), to: endOfToday() },
      showSummary: true,
    },
    {
      id: "last-month",
      value: { from: startOfMonth(subMonths(startOfToday(), 1)), to: endOfMonth(subMonths(startOfToday(), 1)) },
      label: "Last month",
      showSummary: true,
    },
    {
      id: "custom",
      label: "Custom period",
      isManual: true,
      value: null,
    },
  ];

  const items = options.map(option => ({
    key: option.id,
    props: { ...option },
  }));

  const agentEmail = useSelector(selectLoggedInAgentLogin);
  const agentRole = useSelector(selectLoggedInAgentPermission);
  const isReportGenerating = useSelector(getIsReportGenerating);
  const isAdmin = agentRole === Role.Owner;
  const isReportExportUnlocked = useSelector(selectHasReportExportOption);
  const canGenerateReport = isAdmin;

  const [emails, setEmails] = useState(`${agentEmail},\n`);
  const [emailsValidation, setEmailsValidation] = useState("");
  const [range, setRange] = useState(items[0].props.value);
  const [reportExportType, setReportExportType] = useState(ReportExportType.Normal);
  const dispatch = useDispatch();

  const handleSelect = useCallback(({ value: { from, to } }: { value: IDataRangeItemProps["value"] }) => {
    setRange({
      from: startOfDay(from),
      to: endOfDay(to),
    });
  }, []);

  const handleEmailsChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLInputElement>) => {
      setEmails(event.target.value);
    },
    []
  );

  const handleReportTypeChange = useCallback((id: string) => {
    setReportExportType(id as ReportExportType);
  }, []);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      if (!reportExportType || !range || !range.to || !range.from) {
        return;
      }

      event.preventDefault();
      const recipients = emails
        .split(",")
        .map(email => email.trim())
        .filter(Boolean);

      dispatch(
        ReportsViewActions.generateReport(
          reportExportType,
          range.from,
          setMilliseconds(addSeconds(range.to, 1), 0),
          recipients
        )
      );

      trackEngagement("Generate report: Report requested", EventPlace.Reports, {
        exportType: reportExportType == ReportExportType.Normal.toString() ? "Default" : reportExportType.toString(),
        emailsCount: recipients.length,
        from: range.from,
        to: setMilliseconds(addSeconds(range.to, 1), 0),
      });
    },
    [dispatch, reportExportType, emails, range?.from, range?.to]
  );

  const handleEmailsFocus = useCallback(() => {
    setEmailsValidation("");
  }, []);

  const handleEmailsBlur = useCallback(() => {
    setEmailsValidation(validateEmails(emails));
  }, [emails]);

  const rangeSet = range?.from && range.to && isValid(range.from) && isValid(range.to);

  if (!isReportExportUnlocked) {
    return (
      <UpgradeReportPage
        heading="Upgrade and generate detailed reports"
        description="Report exports are only available on Business and higher plans. Analyze ticket trends and agent performance with ease."
      />
    );
  }

  return (
    <PageWrapper>
      <ContentWrapper>
        <SectionHeader size="xs">Export and send report</SectionHeader>
        <SectionDescription>
          Export ticket or agent data to better understand your performance. The report will be emailed as a CSV.{" "}
          <Button
            kind="link-light"
            target="_blank"
            href={`${HelpCenter.HELPDESK_REPORTS}#reports-export`}
            onClick={() => trackEngagement("Generate report: Learn more clicked", EventPlace.Settings)}
          >
            Learn more
          </Button>
        </SectionDescription>
        {!isAdmin && (
          <Block maxWidth="800px" marginTop="24px" marginBottom="24px">
            <Alert kind="info">
              You are not allowed to export data.{" "}
              <StyledLink kind="link-light" to="/agents">
                Contact one of your Admins
              </StyledLink>{" "}
              to do so.
            </Alert>
          </Block>
        )}
        <StyledForm onSubmit={handleSubmit}>
          <TextAreaWrapper labelText="Report type">
            <SegmentedControl
              buttons={[
                { id: ReportExportType.Normal, label: "Ticket performance" },
                { id: ReportExportType.Agents, label: "Agent performance" },
              ]}
              onButtonClick={handleReportTypeChange}
              initialId={ReportExportType.Normal}
            />
          </TextAreaWrapper>

          <RangeDatePicker
            // @ts-expect-error TS(2322)
            options={options}
            onChange={handleSelect}
            initialSelectedItemKey="yesterday"
            toMonth={new Date()}
          >
            {({ select, datepicker, selectedOption, inputs }) => (
              <>
                <TextAreaWrapper labelText="Data range">
                  <StyledSelectField
                    {...select}
                    id="data-range"
                    data-testid="generate-report-date-picker"
                    disabled={!canGenerateReport}
                    required
                    items={items}
                    getItemBody={(props: IDataRangeItemProps) => <DataRangeItem {...props} />}
                    getSelectedItemBody={(props: IDataRangeItemProps) =>
                      props.isManual ? (
                        <DatePickerRangeSelectInputs {...inputs} />
                      ) : (
                        <DataRangeItem {...props} showSummary={false} />
                      )
                    }
                  />
                </TextAreaWrapper>
                {selectedOption && selectedOption.isManual && (
                  <div
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <StyledDatePickerRangeCalendarsWrapper>
                      <DatePicker {...datepicker} />
                    </StyledDatePickerRangeCalendarsWrapper>
                  </div>
                )}
              </>
            )}
          </RangeDatePicker>

          <TextAreaWrapper labelText="Send report to" error={emailsValidation}>
            <StyledTextArea
              id="send-to"
              value={emails}
              disabled={!canGenerateReport}
              onFocus={handleEmailsFocus}
              onBlur={handleEmailsBlur}
              error={!!emailsValidation}
              onChange={handleEmailsChange}
            />
          </TextAreaWrapper>
          <ContentParagraph>
            Note: This report contains sensitive data, check if the provided emails are correct.
          </ContentParagraph>
          <Button
            kind="primary"
            type="submit"
            disabled={!canGenerateReport || validateEmails(emails) !== "" || !rangeSet}
            data-testid="send-report-button"
            loading={isReportGenerating}
          >
            Send report
          </Button>
        </StyledForm>
      </ContentWrapper>
    </PageWrapper>
  );
};

const SectionHeader = styled(Heading)`
  padding-bottom: var(${SpacingToken.Spacing2});
  margin-bottom: var(${SpacingToken.Spacing4});
  box-shadow: var(${ShadowToken.DividerBottom});
`;

const SectionDescription = styled(Text)`
  margin-bottom: var(${SpacingToken.Spacing4});
`;

const ContentParagraph = styled(Text)`
  max-width: 750px;
  margin-bottom: var(${SpacingToken.Spacing4});
`;

const TextAreaWrapper = styled(FormField)`
  width: 100%;
  max-width: 500px;
`;

const StyledTextArea = styled(Textarea)`
  width: 100%;

  textarea {
    width: 100%;
    max-width: 500px;
    min-height: 100px;
    resize: none;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  overflow: auto;
  ${contentPadding};
`;

export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  height: 100%;
`;

const StyledSelectField = styled(SelectField)`
  margin: 0;

  .lc-select-head {
    background-color: var(${DesignToken.SurfacePrimaryDefault});
    color: var(${DesignToken.ContentBasicPrimary});
    border-radius: var(${RadiusToken.Radius3});
  }

  .lc-select-body {
    background-color: var(${DesignToken.PickerListBackground});
    border-radius: var(${RadiusToken.Radius3});
    padding: var(${SpacingToken.Spacing2});
  }

  .lc-select-body__item {
    border-radius: var(${RadiusToken.Radius3});
    color: var(${DesignToken.ContentBasicPrimary});
    margin: var(${SpacingToken.Spacing05}) 0;
  }

  .lc-select-body__item--selected {
    background-color: var(${DesignToken.PickerListOptionBackgroundActive});
  }

  .lc-select-body__item--focused {
    background-color: var(${DesignToken.PickerListOptionBackgroundHover});
  }

  .lc-date-picker--range__select-input {
    max-width: 100px;
  }  
`;

const StyledDatePickerRangeCalendarsWrapper = styled(DatePickerRangeCalendarsWrapper)`
  justify-content: flex-start;
  margin-bottom: var(${SpacingToken.Spacing4});
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

export default GenerateReport;
